import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { environment } from '../../../environment/environment';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface EventMaterialRow {
  event_material_id: number;
  event_id: number;
  material_id: number;
  name: string;
  quantity: number;
  status: string;
  description: string | null;
  admin_notes: string | null;
  instructor_notes: string | null;
  location_id?: number | null;
}

interface MaterialLocationAvailability {
  location_id: number;
  name: string;
  quantity: number;
  remaining: number;
}

interface AvailableMaterial {
  material_id: number;
  name: string;
  total_quantity: number;
  description: string | null;
  remaining_for_event: number;
  by_location?: MaterialLocationAvailability[];
}

interface EventWindow {
  from: string;
  to: string;
}

@Component({
  selector: 'app-duna-materials-event',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: './duna-materials-event.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-materials-event.component.scss'],
})
export class DunaMaterialsEventComponent implements OnInit {
  eventGuid: string | null = null;
  materials: EventMaterialRow[] = [];
  availableMaterials: AvailableMaterial[] = [];
  eventWindow: EventWindow | null = null;
  bufferDays = 0;
  trackStock = true;
  multiLocation = false;
  moduleDisabled = false;
  showMaterialsSection = true;
  loading = false;
  saving = false;
  errorMessage: string | null = null;

  showAddRequest = false;
  addForm = {
    material_id: 0,
    quantity: 1,
    instructor_notes: '',
    location_id: 0 as number,
  };
  editingNotes: Record<number, string> = {};

  constructor(
    private router: Router,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.eventGuid = this.getEventGuidFromUrl();
    if (this.eventGuid) {
      this.load();
    }
  }

  private getEventGuidFromUrl(): string | null {
    const url = (this.router.url || '').split('?')[0];
    const segments = url.split('/');
    const found = segments.find((s) => UUID_REGEX.test(s));
    return found || null;
  }

  load(): void {
    if (!this.eventGuid) return;
    this.loading = true;
    this.errorMessage = null;
    this.http
      .get<{
        materials: EventMaterialRow[];
        available_materials: AvailableMaterial[];
        event_window?: EventWindow;
        settings?: {
          materials_enabled?: boolean;
          has_catalog_materials?: boolean;
          materials_track_stock?: boolean;
          materials_availability_buffer_days?: number;
          materials_multi_location_enabled?: boolean;
        };
      }>(`${environment.apiUrl}/events/${this.eventGuid}/materials`)
      .subscribe({
        next: (res) => {
          this.materials = res.materials || [];
          this.availableMaterials = res.available_materials || [];
          this.eventWindow = res.event_window ?? null;
          const settings = res.settings ?? {};
          this.moduleDisabled = settings.materials_enabled === false;
          const hasCatalog =
            settings.has_catalog_materials ??
            (this.availableMaterials.length > 0 || this.materials.length > 0);
          this.showMaterialsSection =
            !this.moduleDisabled && hasCatalog;
          this.trackStock = settings.materials_track_stock !== false;
          this.bufferDays = settings.materials_availability_buffer_days ?? 0;
          this.multiLocation = !!settings.materials_multi_location_enabled;
          this.editingNotes = {};
          this.materials.forEach((m) => {
            if (m.instructor_notes)
              this.editingNotes[m.event_material_id] = m.instructor_notes;
          });
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          const status = err?.status;
          if (status === 403) {
            this.moduleDisabled = true;
            this.showMaterialsSection = false;
            this.errorMessage =
              err?.error?.message || 'Materielmodulet er ikke aktiveret';
          } else {
            this.errorMessage =
              err?.error?.message || 'Kunne ikke hente materialer';
          }
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  getSelectedMaterial(): AvailableMaterial | undefined {
    return this.availableMaterials.find(
      (a) => a.material_id === this.addForm.material_id
    );
  }

  maxQuantityForAdd(): number {
    const mat = this.getSelectedMaterial();
    if (!mat || !this.trackStock) return 9999;
    if (this.addForm.location_id > 0 && mat.by_location?.length) {
      const loc = mat.by_location.find(
        (l) => l.location_id === this.addForm.location_id
      );
      return loc?.remaining ?? mat.remaining_for_event;
    }
    return mat.remaining_for_event;
  }

  openAddRequest(): void {
    this.showAddRequest = true;
    const notYetRequested = this.getMaterialsNotYetRequested();
    const first = notYetRequested[0];
    this.addForm = {
      material_id: first?.material_id ?? 0,
      quantity: 1,
      instructor_notes: '',
      location_id: 0,
    };
  }

  cancelAddRequest(): void {
    this.showAddRequest = false;
  }

  saveAddRequest(): void {
    if (!this.eventGuid) {
      this.errorMessage = 'Ingen event valgt. Genindlæs siden.';
      return;
    }
    if (!this.addForm.material_id) {
      this.errorMessage = 'Vælg et materiale.';
      return;
    }
    const maxQ = this.maxQuantityForAdd();
    if (this.trackStock && this.addForm.quantity > maxQ) {
      this.errorMessage = `Der er kun ${maxQ} tilgængelige i perioden.`;
      return;
    }
    this.saving = true;
    this.errorMessage = null;
    const body: Record<string, unknown> = {
      material_id: this.addForm.material_id,
      quantity: Math.max(1, this.addForm.quantity),
      instructor_notes: this.addForm.instructor_notes.trim() || undefined,
    };
    if (this.addForm.location_id > 0) {
      body['location_id'] = this.addForm.location_id;
    }
    const url = `${environment.apiUrl}/events/${this.eventGuid}/materials`;
    this.http
      .post<{ event_material_id?: number; message?: string }>(url, body)
      .subscribe({
        next: () => {
          this.saving = false;
          this.showAddRequest = false;
          this.errorMessage = null;
          this.load();
          this.cdr.detectChanges();
        },
        error: (err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            (typeof err?.error === 'string' ? err.error : null);
          this.errorMessage = msg || 'Kunne ikke oprette anmodning';
          this.saving = false;
          this.cdr.detectChanges();
        },
      });
  }

  saveInstructorNotes(eventMaterialId: number, notes: string): void {
    if (!this.eventGuid) return;
    this.http
      .patch(
        `${environment.apiUrl}/events/${this.eventGuid}/materials/${eventMaterialId}`,
        { instructor_notes: notes }
      )
      .subscribe({
        next: () => this.load(),
        error: () => {},
      });
  }

  updateQuantity(eventMaterialId: number, quantity: number): void {
    if (!this.eventGuid || quantity < 1) return;
    this.http
      .patch(
        `${environment.apiUrl}/events/${this.eventGuid}/materials/${eventMaterialId}`,
        { quantity }
      )
      .subscribe({
        next: () => this.load(),
        error: (err) => {
          this.errorMessage =
            err?.error?.message || 'Kunne ikke opdatere antal';
          this.cdr.detectChanges();
        },
      });
  }

  statusLabel(s: string): string {
    const map: Record<string, string> = {
      pending: 'Afventer',
      approved: 'Godkendt',
      rejected: 'Afvist',
    };
    return map[s] || s;
  }

  getMaterialsNotYetRequested(): AvailableMaterial[] {
    const requestedIds = new Set(this.materials.map((m) => m.material_id));
    return this.availableMaterials.filter(
      (a) => !requestedIds.has(a.material_id)
    );
  }

  locationOptionsForMaterial(mat: AvailableMaterial): MaterialLocationAvailability[] {
    return (mat.by_location || []).filter((l) => l.remaining > 0);
  }
}
